// src/components/chat/CitedTextExcerpt.tsx

/**
 * The quoted passage inside a document citation tooltip.
 *
 * A cited unit is whatever the backend parsed the file into: a Word paragraph,
 * a PDF page, a spreadsheet sheet. Pages and sheets arrive as markdown with
 * GFM pipe tables in them, and showing that source text verbatim reads as a
 * wall of pipes. A passage that contains a table is therefore rendered as
 * markdown, with the table capped to a few rows so a large sheet stays a
 * tooltip. Anything else is shown as plain text, shortened the way it always
 * was.
 */

import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Longest plain-text excerpt shown before it is cut at a word boundary. */
const MAX_PLAIN_LENGTH = 200;
/** Body rows shown per table; the rest are counted in a note under it. */
const MAX_TABLE_ROWS = 8;
/** Tables shown per passage; a PDF page can carry several. */
const MAX_TABLES = 2;

/** A pipe-table row: a line that starts and ends with a pipe. */
const TABLE_ROW = /^\s*\|.*\|\s*$/;
/** One cell of the header/body divider GFM requires: dashes with optional colons. */
const DIVIDER_CELL = /^:?-{3,}:?$/;
/** Line breaks the backend writes into spreadsheet cells. */
const CELL_BREAK = /<br\s*\/?>/gi;

/**
 * Truncate text to a maximum length with ellipsis, snapping to the last word
 * boundary so nothing is clipped mid-word.
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  const cut = text.lastIndexOf(" ", maxLength);
  const end = cut > 0 ? cut : maxLength;

  return `${text.substring(0, end)}…`;
};

/** True for the divider line under a table header, such as `| --- | ---: |`. */
const isDividerLine = (line: string): boolean => {
  const trimmed = line.trim();

  if (!trimmed.includes("-")) return false;
  const cells = trimmed.replace(/^\|/, "").replace(/\|$/, "").split("|");

  return cells.length > 0 && cells.every((cell) => DIVIDER_CELL.test(cell.trim()));
};

/** True where a table starts: a pipe row directly followed by a divider line. */
const tableStartFlags = (lines: string[]): boolean[] =>
  lines.map((line, index) => {
    const next = lines.slice(index + 1, index + 2).join("");

    return TABLE_ROW.test(line) && isDividerLine(next);
  });

/** True when the passage holds at least one GFM table. */
export const containsTable = (text: string): boolean =>
  tableStartFlags(text.split("\n")).some(Boolean);

export interface ExcerptBudget {
  /** Body rows kept per table. */
  maxRows?: number;
  /** Tables kept per passage. */
  maxTables?: number;
  /** Characters of prose kept outside tables. */
  maxProse?: number;
}

/**
 * Cut a passage down to tooltip size without breaking its markdown.
 *
 * Every table keeps its header and its first rows; the omitted rows are
 * counted in an italic note right under it. Beyond the table budget, whole
 * tables are counted instead. Prose outside the tables keeps the same
 * character budget a plain quote has, cut at a line boundary.
 */
export const budgetTables = (text: string, budget: ExcerptBudget = {}): string => {
  const maxRows = budget.maxRows ?? MAX_TABLE_ROWS;
  const maxTables = budget.maxTables ?? MAX_TABLES;
  const maxProse = budget.maxProse ?? MAX_PLAIN_LENGTH;
  const lines = text.split("\n");
  const starts = tableStartFlags(lines);
  const out: string[] = [];
  // Rows still allowed in the table being copied, or null outside a table.
  let remaining: number | null = null;
  let omittedRows = 0;
  let tablesSeen = 0;
  let omittedTables = 0;
  let proseLeft = maxProse;
  let proseCut = false;

  const closeTable = (): void => {
    if (omittedRows > 0) {
      out.push("", `*${omittedRows} more ${omittedRows === 1 ? "row" : "rows"} not shown*`);
    }
    remaining = null;
    omittedRows = 0;
  };

  const pushProse = (line: string): void => {
    if (line.trim() === "") {
      out.push(line);

      return;
    }
    if (proseLeft <= 0) {
      if (!proseCut) {
        out.push("…");
        proseCut = true;
      }

      return;
    }
    if (line.length > proseLeft) {
      // The budget runs out inside this line: keep what fits, at a word.
      out.push(truncateText(line, proseLeft));
      proseLeft = 0;
      proseCut = true;

      return;
    }
    out.push(line);
    proseLeft -= line.length;
  };

  lines.forEach((line, index) => {
    const startsTable = starts.slice(index, index + 1).join("") === "true";

    if (startsTable) {
      if (remaining !== null) closeTable();
      tablesSeen += 1;
      if (tablesSeen > maxTables) {
        omittedTables += 1;
        // Skip this table: mark it with a negative budget so its rows drop.
        remaining = -1;

        return;
      }
      // The header row and its divider always stay; the divider is the next line.
      out.push(line);
      remaining = maxRows + 1;

      return;
    }

    if (remaining === null) {
      pushProse(line);

      return;
    }

    if (!TABLE_ROW.test(line)) {
      closeTable();
      pushProse(line);

      return;
    }

    if (remaining > 0) {
      out.push(line);
      remaining -= 1;
    } else if (remaining === 0) {
      omittedRows += 1;
    }
  });

  if (remaining !== null) closeTable();
  if (omittedTables > 0) {
    out.push("", `*${omittedTables} more ${omittedTables === 1 ? "table" : "tables"} not shown*`);
  }

  return out.join("\n");
};

const textStyle: React.CSSProperties = {
  fontSize: "11px",
  lineHeight: "1.5",
  color: "#757575",
  fontStyle: "italic",
  wordBreak: "break-word",
};

const blockStyle: React.CSSProperties = { margin: "0 0 4px 0" };

const headingStyle: React.CSSProperties = {
  ...blockStyle,
  fontWeight: 600,
  fontStyle: "normal",
};

const tableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  fontSize: "10px",
  lineHeight: "1.4",
  fontStyle: "normal",
  color: "#616161",
  margin: "2px 0 4px 0",
  maxWidth: "100%",
};

const cellStyle: React.CSSProperties = {
  border: "1px solid #E0E0E0",
  padding: "1px 5px",
  textAlign: "left",
  verticalAlign: "top",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "140px",
};

const headerCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight: 600,
  backgroundColor: "#F5F5F5",
  color: "#424242",
};

type Children = { children?: React.ReactNode };

const Heading: React.FC<Children> = ({ children }) => <div style={headingStyle}>{children}</div>;

/**
 * Element overrides that keep the markdown at tooltip scale: headings become
 * short bold lines, paragraphs keep the quote style, and links are plain text.
 */
const markdownComponents = {
  h1: Heading,
  h2: Heading,
  h3: Heading,
  h4: Heading,
  h5: Heading,
  h6: Heading,
  p: ({ children }: Children) => <div style={blockStyle}>{children}</div>,
  a: ({ children }: Children) => <span>{children}</span>,
  table: ({ children }: Children) => (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>{children}</table>
    </div>
  ),
  th: ({ children }: Children) => <th style={headerCellStyle}>{children}</th>,
  td: ({ children }: Children) => <td style={cellStyle}>{children}</td>,
};

export interface CitedTextExcerptProps {
  text: string;
}

/** The quote of a document citation, as plain text or as a capped table. */
export const CitedTextExcerpt: React.FC<CitedTextExcerptProps> = ({ text }) => {
  if (!containsTable(text)) {
    return (
      <div className="doc-citation-excerpt" style={textStyle}>
        {truncateText(text, MAX_PLAIN_LENGTH)}
      </div>
    );
  }

  const markdown = budgetTables(text.replace(CELL_BREAK, " "));

  return (
    <div className="doc-citation-excerpt" style={textStyle}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default CitedTextExcerpt;
